import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    MenuItem,
    Alert,
    CircularProgress,
    Typography,
    Box,
    InputAdornment
} from '@mui/material';
import {
    Warning as WarningIcon,
    Error as ErrorIcon,
    Schedule as ScheduleIcon,
    BugReport as BugReportIcon,
    FindInPage as FindInPageIcon,
    Build as BuildIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';

const ADJUSTMENT_REASONS = {
    expired: {
        label: 'Expired Products',
        icon: ScheduleIcon,
        color: 'error',
        description: 'Products past expiry date',
        requiresApproval: false
    },
    damaged: {
        label: 'Damaged Items',
        icon: ErrorIcon,
        color: 'error', 
        description: 'Physically damaged products',
        requiresApproval: false
    },
    recalled: {
        label: 'Product Recall',
        icon: WarningIcon,
        color: 'warning',
        description: 'Manufacturer or supplier recall',
        requiresApproval: false
    },
    theft: {
        label: 'Theft/Missing',
        icon: BugReportIcon,
        color: 'error',
        description: 'Stolen or unaccounted items',
        requiresApproval: false
    },
    found: {
        label: 'Found Items',
        icon: FindInPageIcon,
        color: 'success',
        description: 'Previously missing items found',
        requiresApproval: false
    },
    correction: {
        label: 'Count Correction',
        icon: BuildIcon,
        color: 'info',
        description: 'Physical count vs system discrepancy',
        requiresApproval: false
    },
    customer_return: {
        label: 'Customer Return',
        icon: FindInPageIcon,
        color: 'info',
        description: 'Customer returned items',
        requiresApproval: false
    },
    quality_issue: {
        label: 'Quality Issue',
        icon: WarningIcon,
        color: 'warning',
        description: 'Quality control failure',
        requiresApproval: false
    }
};

const InventoryAdjustment = ({ open, onClose, product, onAdjustmentComplete, branchId }) => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [formData, setFormData] = useState({
        adjustmentType: 'decrease',
        quantity: '',
        reason: '',
        notes: '',
        autoApprove: false
    });
    const [selectedReason, setSelectedReason] = useState(null);
    
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        if (name === 'reason') {
            setSelectedReason(ADJUSTMENT_REASONS[value] || null);
            // Auto-approve all adjustments (no restrictions)
            setFormData(prev => ({
                ...prev,
                autoApprove: true
            }));
        }
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        
        try {
            const adjustmentQuantity = formData.adjustmentType === 'decrease' 
                ? -parseFloat(formData.quantity) 
                : parseFloat(formData.quantity);
                
            await api.post(`/branches/${branchId || user.assignedBranches?.[0]?.id}/adjustments`, {
                productId: product.product_id,
                adjustmentType: formData.reason, // Use specific reason as adjustment type
                quantityAdjusted: adjustmentQuantity,
                reason: `${ADJUSTMENT_REASONS[formData.reason]?.label}: ${formData.notes}`,
                autoApprove: formData.autoApprove
            });
            
            onAdjustmentComplete();
            onClose();
        } catch (error) {
            console.error('Error making adjustment:', error);
            setError(error.response?.data?.message || 'Failed to make adjustment');
        } finally {
            setLoading(false);
        }
    };
    
    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
        >
            <form onSubmit={handleSubmit}>
                <DialogTitle sx={{ pb: 1 }}>
                    Stock Adjustment
                </DialogTitle>
                
                <DialogContent>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}
                    
                    <Box sx={{ mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
                        <Typography variant="h6" color="primary" sx={{ mb: 1 }}>
                            {product?.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            SKU: {product?.sku} | Current Stock: <strong>{product?.quantity || 0} units</strong>
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Unit Cost: KES {Number(product?.unit_cost || 0).toFixed(2)}
                        </Typography>
                    </Box>
                    
                    {selectedReason && (
                        <Alert 
                            severity="info"
                            sx={{ mb: 2 }}
                            icon={React.createElement(selectedReason.icon)}
                        >
                            <strong>{selectedReason.label}</strong>: {selectedReason.description}
                        </Alert>
                    )}
                    
                    <Grid container spacing={2}>
                        <Grid size={6}>
                            <TextField
                                fullWidth
                                select
                                required
                                label="Adjustment Type"
                                name="adjustmentType"
                                value={formData.adjustmentType}
                                onChange={handleChange}
                                disabled={loading}
                            >
                                <MenuItem value="decrease">- Decrease Stock</MenuItem>
                                <MenuItem value="increase">+ Increase Stock</MenuItem>
                            </TextField>
                        </Grid>
                        
                        <Grid size={6}>
                            <TextField
                                fullWidth
                                required
                                type="number"
                                label="Quantity"
                                name="quantity"
                                value={formData.quantity}
                                onChange={handleChange}
                                disabled={loading}
                                inputProps={{
                                    min: 1,
                                    max: formData.adjustmentType === 'decrease' ? product?.quantity : 9999
                                }}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">units</InputAdornment>
                                }}
                                helperText={
                                    formData.adjustmentType === 'decrease' 
                                        ? `Max: ${product?.quantity || 0}`
                                        : 'Units to add'
                                }
                            />
                        </Grid>
                        
                        <Grid size={12}>
                            <TextField
                                fullWidth
                                select
                                required
                                label="Reason for Adjustment"
                                name="reason"
                                value={formData.reason}
                                onChange={handleChange}
                                disabled={loading}
                                helperText="Select the reason for adjustment"
                            >
                                {Object.entries(ADJUSTMENT_REASONS).map(([value, data]) => {
                                    const IconComponent = data.icon;
                                    return (
                                        <MenuItem key={value} value={value}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <IconComponent fontSize="small" color={data.color} />
                                                <span>{data.label}</span>
                                            </Box>
                                        </MenuItem>
                                    );
                                })}
                            </TextField>
                        </Grid>
                        
                        <Grid size={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Additional Notes"
                                name="notes"
                                value={formData.notes}
                                onChange={handleChange}
                                disabled={loading}
                                placeholder="Provide detailed explanation"
                                required
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            Note
                                        </InputAdornment>
                                    )
                                }}
                                helperText="Detailed notes help with audit trails"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button 
                        onClick={onClose}
                        disabled={loading}
                        variant="outlined"
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        color={formData.adjustmentType === 'decrease' ? 'error' : 'success'}
                        disabled={loading || !formData.quantity || !formData.reason || !formData.notes}
                        startIcon={selectedReason?.icon ? React.createElement(selectedReason.icon) : <WarningIcon />}
                    >
                        {loading ? (
                            <CircularProgress size={24} />
                        ) : (
                            `${formData.adjustmentType === 'decrease' ? 'Remove' : 'Add'} ${formData.quantity || 0} Units`
                        )}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default InventoryAdjustment; 